import { ArrowUpDown } from 'lucide-react';
import { useCallback, useState } from 'react';

import type { TaskSummary } from '@/lib/api/types';
import { useT, type MessageKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export const TASK_SORTS = [
  'default',
  'postponed',
  'priority',
  'project',
  'parent',
] as const;

export type TaskSort = (typeof TASK_SORTS)[number];

const LABELS: Record<TaskSort, MessageKey> = {
  default: 'sort.default',
  postponed: 'sort.postponed',
  priority: 'sort.priority',
  project: 'sort.project',
  parent: 'sort.parent',
};

const PRIORITY_RANK: Record<string, number> = {
  URGENT: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
  NONE: 4,
};

/**
 * Remembered per screen, because the question each screen answers differs:
 * the backlog is read by "what have I dodged the most", a label by project.
 * Losing the choice on every navigation made it not worth making.
 */
export function useTaskSort(screen: string): [TaskSort, (next: TaskSort) => void] {
  const key = `mirondo.sort.${screen}`;
  const [sort, setSort] = useState<TaskSort>(() => {
    const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
    return (TASK_SORTS as readonly string[]).includes(stored ?? '')
      ? (stored as TaskSort)
      : 'default';
  });
  const choose = useCallback(
    (next: TaskSort) => {
      setSort(next);
      try {
        localStorage.setItem(key, next);
      } catch {
        // Private mode, a full quota — the sort still works for this visit.
      }
    },
    [key],
  );
  return [sort, choose];
}

/**
 * Sorts a page of tasks without touching the server's order underneath.
 *
 * Every mode is a *stable* reordering: within equal keys the list keeps the
 * order the server chose, so switching to "by project" and back leaves the
 * screen where it was rather than shuffling it.
 */
export function sortTasks(tasks: TaskSummary[], sort: TaskSort): TaskSummary[] {
  if (sort === 'default') return tasks;
  const rows = tasks.map((task, index) => ({ task, index }));
  const by = (rank: (task: TaskSummary) => number | string) =>
    rows
      .sort((a, b) => {
        const left = rank(a.task);
        const right = rank(b.task);
        if (left === right) return a.index - b.index;
        return left < right ? -1 : 1;
      })
      .map((row) => row.task);

  switch (sort) {
    case 'postponed':
      // Most-dodged first: the whole point of counting is to see the top.
      return by((task) => -task.postponed_count);
    case 'priority':
      return by((task) => PRIORITY_RANK[task.priority] ?? 9);
    case 'project':
      // Unfiled last — it is the absence of an answer, not an answer that
      // happens to sort before "Astra".
      return by((task) => task.project_name?.toLocaleLowerCase() ?? '￿');
    case 'parent':
      return by((task) => task.parent_title?.toLocaleLowerCase() ?? '￿');
    default:
      return tasks;
  }
}

/** A row of choices rather than a select: four options, all worth seeing. */
export function TaskSortBar({
  sort,
  onChange,
}: {
  sort: TaskSort;
  onChange: (next: TaskSort) => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <ArrowUpDown className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
      <span className="sr-only">{t('sort.label')}</span>
      {TASK_SORTS.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={sort === option}
          className={cn(
            'rounded-full px-2.5 py-1 text-xs transition-surface',
            sort === option
              ? 'bg-accent/10 font-medium text-accent'
              : 'text-content-muted hover:bg-surface-hover hover:text-content',
          )}
        >
          {t(LABELS[option])}
        </button>
      ))}
    </div>
  );
}
